import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowUpRight, FiMenu, FiX } = FiIcons;

const navItems = [
  { label: 'The studio', id: 'constellation' },
  { label: 'Approach', id: 'approach' },
  { label: 'Current orbits', id: 'work' }
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const sectionHref = (id) => location.pathname === '/' ? `#${id}` : `/#${id}`;
  const useSolidBackground = scrolled || isMenuOpen || location.pathname !== '/';

  return (
    <header
      className={`fixed top-0 z-50 w-full border-b transition-all duration-300 ${
        useSolidBackground
          ? 'border-[#f5f0e8]/15 bg-[#0b1112]/95 shadow-2xl shadow-black/10'
          : 'border-transparent bg-[#0b1112]/55'
      } backdrop-blur-md`}
      role="banner"
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-4 sm:px-10 lg:px-16">
        <Link
          to="/"
          className="group flex items-center gap-3 text-[#f5f0e8]"
          aria-label="Celestia Fortis home"
        >
          <img
            src="/brand/celestia-fortis-logo.png"
            alt=""
            className="studio-logo-mark"
            aria-hidden="true"
          />
          <span className="hidden font-serif text-xl tracking-[-0.02em] sm:inline">Celestia Fortis</span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex" aria-label="Main navigation">
          {navItems.map((item) => (
            <a key={item.id} href={sectionHref(item.id)} className="studio-nav-link">
              {item.label}
            </a>
          ))}
          <Link to="/book" className="studio-nav-cta">
            Conversation
            <SafeIcon icon={FiArrowUpRight} className="h-4 w-4" aria-hidden="true" />
          </Link>
        </nav>

        <button
          type="button"
          className="rounded-full border border-[#f5f0e8]/20 p-2 text-[#f5f0e8] transition-colors hover:border-[#d49a55] hover:text-[#d49a55] md:hidden"
          onClick={() => setIsMenuOpen((open) => !open)}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen}
          aria-controls="mobile-menu"
        >
          <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t border-[#f5f0e8]/10 bg-[#0b1112] md:hidden"
            role="navigation"
            aria-label="Mobile navigation"
          >
            <div className="mx-auto flex max-w-[1440px] flex-col gap-2 px-6 py-6 sm:px-10">
              {navItems.map((item) => (
                <a
                  key={item.id}
                  href={sectionHref(item.id)}
                  className="border-b border-[#f5f0e8]/10 py-3 font-serif text-2xl text-[#f5f0e8] transition-colors hover:text-[#d49a55]"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </a>
              ))}
              <Link
                to="/book"
                className="mt-4 flex items-center justify-center gap-2 rounded-full bg-[#d49a55] px-5 py-3 text-sm font-bold text-[#0b1112]"
                onClick={() => setIsMenuOpen(false)}
              >
                Start a conversation
                <SafeIcon icon={FiArrowUpRight} className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
